import type { FlattenedSectionProps } from "../../../../../components/Questionnaire/Section";
import type { QuestionProps } from "../../../../../components/Questionnaire/Section/Question/types";
import { ERRORS } from "../../../../../constants/errors";
import type { Locale } from "../../../../../types";
import { formatAnswer } from "../../../../questionnaire/utils/formatAnswer";
import type { AddressModes, Answers } from "../../../types";
import { isObject } from "../../../../../utils";

type ProcessQuestionnaireArgs = {
  flattenedSections: FlattenedSectionProps[];
  existingAnswers: Answers;
  groupChildrenVisibility?: Record<string, boolean>;
  groupQuestionNames: Set<string>;
  locale?: Locale;
};

type ProcessQuestionnaireResult = {
  sections: FlattenedSectionProps[];
  addressModes: AddressModes;
  visibility: Record<string, boolean>;
  serverVisibility: Record<string, boolean>;
  answers: Answers;
  serverErrors: Record<string, string>;
  firstIncompleteSection: number | undefined;
  firstSectionWithErrors: number | undefined;
};

/** Compute visibility for a question based on group membership and address modes */
const computeVisibility = (
  q: QuestionProps,
  groupQuestionNames: Set<string>,
  groupChildrenVisibility: Record<string, boolean> | undefined,
  addressModes: AddressModes,
): boolean => {
  if (groupQuestionNames.has(q.name)) {
    return groupChildrenVisibility?.[q.name] ?? false;
  }
  if (q.associatedQuestionName) {
    const mode = addressModes[q.associatedQuestionName];
    if (mode === "lookup") return false;
    if (mode === "manual") return true;
    if (mode === "none") return false; //Case: parent address lookup is hidden.
  }
  return q.visible ?? q.suggested ?? false;
};

/** Initialise answer from question data, mutating the answers object */
const initialiseAnswer = (
  q: QuestionProps,
  answers: Answers,
  locale: Locale | undefined,
): void => {
  if (q.answer === undefined) return;
  const formattedAnswer = formatAnswer(q, q.answer, locale);
  if (isObject(formattedAnswer)) {
    Object.assign(answers, formattedAnswer);
  } else {
    answers[q.name] = formattedAnswer;
  }
};

/** Get updated question props for manual address fields */
const getAddressFieldUpdate = (
  q: QuestionProps,
  addressModes: AddressModes,
): QuestionProps | null => {
  if (!q.associatedQuestionName) return null;
  const mode = addressModes[q.associatedQuestionName];
  if (mode === "manual") return { ...q, answer: "", visible: true };
  if (mode === "lookup") return { ...q, visible: false };
  return null;
};

/**
 * Single-pass processor that combines:
 * - setAddressModes
 * - getVisibilityMap
 * - initialiseAnswers
 * - getServerQuestionErrors
 * - getFirstIncompleteSection
 * - getFirstSectionWithErrors
 *
 * Reduces from 6 separate O(n) passes to 2 passes:
 * 1. Build addressModes (need full scan first)
 * 2. Process everything else in one pass
 */
export const processQuestionnaire = ({
  flattenedSections,
  existingAnswers,
  groupChildrenVisibility,
  groupQuestionNames,
  locale,
}: ProcessQuestionnaireArgs): ProcessQuestionnaireResult => {
  // Pass 1: Build addressModes (need this before processing visibility)
  const addressModes: AddressModes = {};
  for (const section of flattenedSections) {
    for (const q of section.questions) {
      if (q.display?.type === "address_lookup") {
        addressModes[q.name] = q.visible ? "lookup" : "none";
      }
    }
  }

  // Pass 2: Process everything else in a single pass
  const visibility: Record<string, boolean> = {};
  const serverVisibility: Record<string, boolean> = {};
  const answers: Answers = { ...existingAnswers };
  const serverErrors: Record<string, string> = {};
  let firstIncompleteSection: number | undefined;
  let firstSectionWithErrors: number | undefined;

  const sections: FlattenedSectionProps[] = flattenedSections.map(
    (section, sectionIdx) => {
      let sectionHasError = false;
      let sectionHasIncomplete = false;

      const questions = section.questions.map((q: QuestionProps) => {
        if (!q.name) return q;

        // Capture the original server-provided visibility before any stamping
        serverVisibility[q.name] = q.visible ?? false;

        const questionVisible = computeVisibility(
          q,
          groupQuestionNames,
          groupChildrenVisibility,
          addressModes,
        );
        visibility[q.name] = questionVisible;

        initialiseAnswer(q, answers, locale);

        if (q.showError && questionVisible) {
          serverErrors[q.name] = q.errorMessage || ERRORS.INVALID_GENERIC;
          sectionHasError = true;
        }

        if (questionVisible && q.required && !answers[q.name]) {
          sectionHasIncomplete = true;
        }

        // Address fields handle their own visibility internally.
        const addressFieldUpdate = getAddressFieldUpdate(q, addressModes);
        if (addressFieldUpdate) {
          return addressFieldUpdate;
        }

        // Stamp visible on the question so props.visible is a reliable
        // hydration fallback before the Jotai visibility atom is available.
        const needsVisibilityUpdate = q.visible !== questionVisible;
        if (needsVisibilityUpdate) {
          return { ...q, visible: questionVisible };
        }

        return q;
      });

      if (sectionHasIncomplete && firstIncompleteSection === undefined) {
        firstIncompleteSection = sectionIdx;
      }
      if (sectionHasError && firstSectionWithErrors === undefined) {
        firstSectionWithErrors = sectionIdx;
      }

      return { ...section, questions };
    },
  );

  return {
    sections,
    addressModes,
    visibility,
    serverVisibility,
    answers,
    serverErrors,
    firstIncompleteSection,
    firstSectionWithErrors,
  };
};
